import ButtonUnstyled from '@mui/base/ButtonUnstyled';
import ExitToApp from '@mui/icons-material/ExitToApp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Security from '@mui/icons-material/Security';
import { Avatar, Box, capitalize, Menu, MenuItem, Button, IconButton, Tooltip } from '@mui/material';
import ApiAuthService from 'api/AuthApiService';
import NotificationApiService from 'api/NotificationApiService';
import { stringToColor } from 'helpers/format';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { injectAlertsAction, loadTokenAction } from 'store/actions/appActions';
// import { openAiscreen } from 'store/actions/aiActions';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import User from 'types/entities/User';
import Alert from 'types/models/Alert';
// import aiButton from 'assets/img/icons/aiButton.svg';
import helpButton from 'assets/img/icons/help.svg';
import { Notification } from './Notifications';
import './UserTopBar.scss';
import { RootState } from 'store/redux';
import { getSubdomain } from 'helpers/utils';
import getCompanyByHeader from 'helpers/getCompanyByHeader';

type UserTopBarProps = {
  activeUser: User;
};
const UserTopBar = (props: UserTopBarProps) => {
  const {
    activeUser: { avatar_uri, first_name, last_name, id },
  } = props;
  const { dispatch } = useAppState();
  const [t] = useTranslation();
  const listInnerRef = useRef<HTMLDivElement>(null);

  const alerts = useSelector((state) => state.app.alerts);
  const aiState = useSelector((state: RootState) => state.ai);
  const userState = useSelector((state) => state.app.user);

  const [currNotificationPage, setCurrNotificationPage] = useState(0);
  const [prevNotificationPage, setPrevNotificationPage] = useState(-1);
  const [lastNotificationList, setLastNotificationList] = useState(false);

  const [isAlertsLoading, setIsAlertsLoading] = useState(false);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleOnScroll = () => {
    if (listInnerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listInnerRef.current;
      if (scrollTop + clientHeight === scrollHeight && !isAlertsLoading) {
        setCurrNotificationPage(currNotificationPage + 1);
      }
    }
  };

  const shouldHideAIAgent = () => {
    const currentHostname = window.location.hostname;
    const currentSub = getSubdomain(currentHostname);
    const company = getCompanyByHeader(currentSub);
    return company ? !company.aiAgentEnabled : false;
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsAlertsLoading(true);
      const notification = await NotificationApiService.Instance.getNotifications(id, currNotificationPage);
      if (!notification.data.datas.length) {
        setLastNotificationList(true);
        setIsAlertsLoading(false);
        return;
      }
      setPrevNotificationPage(currNotificationPage);
      setAlerts([...alerts, ...notification.data.datas]);
      setIsAlertsLoading(false);
    };
    if (
      !lastNotificationList &&
      prevNotificationPage !== currNotificationPage &&
      currNotificationPage > 0 &&
      !isAlertsLoading
    ) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currNotificationPage, lastNotificationList, prevNotificationPage]);

  const buttonDisconnectText = t('Disconnect');

  const setAlerts = (alerts: Alert[]) => {
    dispatch(injectAlertsAction(alerts));
  };

  const handleDisconnect = async () => {
    console.log('handleDisconnect called');
    dispatch(loadTokenAction()); // Clear Redux state

    try {
      // Call centralized logout method (revokes token and clears all storage)
      const logoutUri = await ApiAuthService.logout();
      // Clear MFA session storage on logout (in addition to clearAllAuthData)
      sessionStorage.removeItem('mfa_check_done');
      sessionStorage.removeItem('mfa_check_result');
      window.location.href = logoutUri;
    } catch (error) {
      console.error('Logout failed:', error);
      // Redirect to login on error
      window.location.href = await ApiAuthService.getAuthAuthorization();
    }
  };
  const mountedRef = useRef<boolean>(true);

  useEffect(() => {
    async function getAlerts() {
      if (!mountedRef.current) return;
      const notification = await NotificationApiService.Instance.getNotifications(id, 0);
      if (notification && notification.data) setAlerts(notification.data.datas);
    }
    if (!alerts.length) {
      getAlerts();
    }
    const interval = setInterval(() => getAlerts(), 100000);
    clearInterval(interval);

    return () => {
      mountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const toggleWidgetVisibility = () => {
    window.FreshworksWidget('open');
  };

  return (
    <>
      <div className="user-navbar">
        <Tooltip placement="top" title={t('help') as string}>
          <Button
            variant="contained"
            onClick={toggleWidgetVisibility}
            className="toggle-widget-button"
            sx={{
              display: shouldHideAIAgent() ? 'none' : 'inline-flex',
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'transparent', // Make background transparent on hover
                color: 'primary.main', // Change text color to match the original button color
                boxShadow: 'none', // Remove the default hover shadow for contained variant
                // You might want a border on hover to define the button's shape
                border: 'none',
              },
            }}
          >
            {/* {isWidgetVisible ? 'Hide' : 'Show'} Help */}
            <img src={helpButton} alt="help menu" />
          </Button>
        </Tooltip>

        <Notification
          alerts={alerts}
          setAlerts={(idAlert: number) =>
            setAlerts(alerts.map((alert) => (alert.id !== idAlert ? alert : { ...alert, is_read: true })))
          }
          markAllAsReads={() => setAlerts(alerts.map((alert) => ({ ...alert, is_read: true })))}
          handleOnScroll={handleOnScroll}
          listRef={listInnerRef}
          isLoading={isAlertsLoading}
        />
        <ButtonUnstyled aria-haspopup="true" onClick={handleClick} className="user-menu">
          {!avatar_uri && (
            <Avatar
              style={{
                backgroundColor: stringToColor(first_name),
                color: stringToColor(first_name, 'color'),
              }}
              className="user-menu__avatar"
              variant="rounded"
            >{` ${capitalize(first_name.charAt(0))}${capitalize(last_name.charAt(0))}`}</Avatar>
          )}
          {avatar_uri && <Avatar src={avatar_uri} alt={t('Active_user_avatar')} />}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <span className="user-menu__name">
              {`${capitalize(first_name.toLowerCase())} ${capitalize(last_name.toLowerCase())}`}
            </span>
          </Box>
          <KeyboardArrowDownIcon />
        </ButtonUnstyled>
        <Menu
          anchorEl={anchorEl}
          open={!!anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          onBackdropClick={() => setAnchorEl(null)}
        >
          <MenuItem onClick={() => handleDisconnect()}>
            {buttonDisconnectText}
            <ExitToApp color="primary" />
          </MenuItem>
        </Menu>
      </div>
    </>
  );
};
export default UserTopBar;
